import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import {
  Lock, Search, ArrowRight, Coins, Zap, Heart, Sparkles,
  ShoppingCart, DollarSign, Megaphone, UserCheck, TrendingUp, Settings, Briefcase,
  ChevronRight, Layers, Gift, Users, Copy, Check, Share2
} from "lucide-react";
import axios from "axios";
import BookmarkButton from "@/components/BookmarkButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICON_MAP = { ShoppingCart, DollarSign, Megaphone, UserCheck, TrendingUp, Settings, Briefcase };

const COLOR_CLASSES = {
  blue:   { icon: "bg-blue-100 text-blue-600",      badge: "bg-blue-100 text-blue-700",   chip: "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100" },
  green:  { icon: "bg-emerald-100 text-emerald-600", badge: "bg-emerald-100 text-emerald-700", chip: "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100" },
  purple: { icon: "bg-purple-100 text-purple-600",   badge: "bg-purple-100 text-purple-700",   chip: "bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-100" },
  orange: { icon: "bg-orange-100 text-orange-600",   badge: "bg-orange-100 text-orange-700",   chip: "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-100" },
  red:    { icon: "bg-red-100 text-red-600",         badge: "bg-red-100 text-red-700",     chip: "bg-red-50 text-red-700 border-red-100 hover:bg-red-100" },
  teal:   { icon: "bg-teal-100 text-teal-600",       badge: "bg-teal-100 text-teal-700",   chip: "bg-teal-50 text-teal-700 border-teal-100 hover:bg-teal-100" },
  indigo: { icon: "bg-indigo-100 text-indigo-600",   badge: "bg-indigo-100 text-indigo-700",   chip: "bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100" },
};

const AI_TOOLS = [
  { name: "ChatGPT",           color: "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100", dot: "bg-emerald-500", desc: "OpenAI · Analysis" },
  { name: "Claude",            color: "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100",    dot: "bg-orange-500",  desc: "Anthropic · Writing" },
  { name: "Gemini",            color: "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100",            dot: "bg-blue-500",    desc: "Google · Research" },
  { name: "Microsoft Copilot", color: "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100",                dot: "bg-sky-500",     desc: "Microsoft · Office" },
  { name: "Perplexity",        color: "bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100",            dot: "bg-teal-500",    desc: "Perplexity · Search" },
];

const FavouriteCard = ({ app, navigate, onRemove, user }) => {
  const isPremium = app.is_premium;
  const variations = app.prompt_variations ? Object.keys(app.prompt_variations) : [];
  const isLocked = isPremium && user && user.subscription_plan !== "full" &&
    (user.accessible_functions !== "all") &&
    !(user.accessible_functions || []).includes(app.function_id);

  return (
    <div
      className={`relative border border-slate-200 rounded-xl p-4 cursor-pointer bg-white hover:border-blue-200 hover:shadow-sm transition-all group ${isLocked ? "opacity-80 hover:opacity-100" : ""}`}
      onClick={() => navigate(`/app/${app.app_id}`)}
      data-testid={`favourite-card-${app.app_id}`}
    >
      <div className="absolute top-2.5 right-2.5">
        <BookmarkButton appId={app.app_id} initialState={true} onToggle={(_, state) => { if (!state) onRemove(app.app_id); }} size={14} />
      </div>
      <p className="text-xs text-slate-400 capitalize mb-1">{app.function_id}</p>
      <h4 className="text-sm font-semibold text-slate-900 pr-6 leading-snug mb-2.5 line-clamp-2 flex items-center gap-1.5">
        {isLocked && <Lock size={12} className="text-slate-400 flex-shrink-0" />}
        {app.title}
      </h4>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge className={`text-xs border-0 ${isPremium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          {isPremium ? `${app.credit_cost} cr` : "FREE"}
        </Badge>
        {variations.length > 0 && !isLocked && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-medium flex items-center gap-1">
            <Sparkles size={9} /> {variations.length} tool {variations.length === 1 ? "version" : "versions"}
          </span>
        )}
        {isLocked && <span className="text-xs text-amber-600 font-medium flex items-center gap-1"><Lock size={10} /> Upgrade required</span>}
      </div>
    </div>
  );
};

// ── Gift Code Section ─────────────────────────────────────────────────────────
const GiftCodeSection = ({ API, user, onCreditsUpdated }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRedeem = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/gift-codes/redeem`, { code: code.trim() }, { withCredentials: true });
      setResult({ success: true, message: res.data.message });
      setCode("");
      onCreditsUpdated(res.data.user_credits);
      toast.success("Code redeemed!", { description: res.data.message });
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to redeem code.";
      setResult({ success: false, message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="gift-code-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Gift size={16} className="text-amber-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Redeem Gift Code</h3>
          <p className="text-xs text-slate-500">Enter a code to add credits or unlock access</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="e.g. GC-PROMO2024"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === "Enter" && handleRedeem()}
          className="font-mono tracking-widest uppercase text-sm"
          data-testid="gift-code-input"
        />
        <Button
          onClick={handleRedeem}
          disabled={loading || !code.trim()}
          className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0"
          data-testid="redeem-gift-code-btn"
        >
          {loading ? "..." : "Redeem"}
        </Button>
      </div>
      {result && (
        <p className={`text-xs mt-2 font-medium ${result.success ? "text-emerald-600" : "text-red-500"}`}
          data-testid="gift-code-result">
          {result.message}
        </p>
      )}
    </div>
  );
};

// ── Referral Section ──────────────────────────────────────────────────────────
const ReferralSection = ({ API, user }) => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const frontendUrl = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;

  useEffect(() => {
    axios.get(`${API}/referrals/my-info`, { withCredentials: true })
      .then(r => setInfo(r.data))
      .catch(() => {});
  }, [API]);

  const referralLink = info?.referral_code
    ? `${window.location.origin}/auth?ref=${info.referral_code}&tab=register`
    : "";

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied!", { description: "Share it with colleagues to earn credits." });
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="referral-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Users size={16} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Invite Friends & Earn Credits</h3>
          <p className="text-xs text-slate-500">Get 20 credits for every colleague who joins</p>
        </div>
      </div>
      {info ? (
        <>
          <div className="flex gap-2 mb-3">
            <Input
              readOnly
              value={referralLink}
              className="text-xs text-slate-500 bg-slate-50 font-mono"
              data-testid="referral-link-input"
            />
            <Button
              variant="outline"
              onClick={handleCopy}
              className={`flex-shrink-0 transition-colors ${copied ? "border-emerald-300 text-emerald-600" : ""}`}
              data-testid="copy-referral-link-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <p className="font-bold text-slate-900 text-lg" data-testid="referral-count">{info.referral_count}</p>
              <p className="text-xs text-slate-500">Referrals</p>
            </div>
            <div className="w-px h-8 bg-slate-100" />
            <div className="text-center">
              <p className="font-bold text-blue-600 text-lg" data-testid="referral-credits-earned">{info.referral_credits_earned}</p>
              <p className="text-xs text-slate-500">Credits earned</p>
            </div>
            <div className="w-px h-8 bg-slate-100" />
            <div className="text-center">
              <p className="font-bold text-slate-500 text-xs font-mono" data-testid="referral-code">{info.referral_code}</p>
              <p className="text-xs text-slate-500">Your code</p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700">
            <span className="font-semibold">Milestones:</span> 5 referrals = +50 bonus credits · 10 referrals = +100 bonus credits
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      )}
    </div>
  );
};

// Function card with subcategory chips below
const FunctionCard = ({ fn, subcats, onSubcatClick, onCardClick }) => {
  const Icon = ICON_MAP[fn.icon] || Briefcase;
  const colors = COLOR_CLASSES[fn.color] || COLOR_CLASSES.blue;
  const locked = !fn.has_access;
  const CHIPS_LIMIT = 5;
  const visibleSubcats = subcats.slice(0, CHIPS_LIMIT);
  const hiddenCount = subcats.length - CHIPS_LIMIT;

  return (
    <div
      className={`relative rounded-xl border bg-white p-5 card-shadow fade-in transition-all ${locked ? "opacity-80" : ""}`}
      data-testid={`function-card-${fn.function_id}`}
    >
      {locked && <div className="absolute top-3 right-3"><Lock size={14} className="text-slate-400" /></div>}

      {/* Clickable top area */}
      <div className="cursor-pointer hover:opacity-90 transition-opacity" onClick={onCardClick}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colors.icon}`}>
          <Icon size={20} />
        </div>
        <h3 className="font-semibold text-slate-900 mb-1 text-sm">{fn.name}</h3>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed line-clamp-2">{fn.description}</p>
      </div>

      {/* Subcategory chips */}
      {subcats.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {visibleSubcats.map(s => (
            <button
              key={s.subcategory_id}
              onClick={e => { e.stopPropagation(); onSubcatClick(s); }}
              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${colors.chip}`}
              disabled={locked && fn.function_id !== "general"}
              data-testid={`subcat-chip-${s.subcategory_id}`}
            >
              {s.name}
            </button>
          ))}
          {hiddenCount > 0 && (
            <button
              onClick={e => { e.stopPropagation(); onCardClick(); }}
              className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 transition-all"
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      )}

      {/* Bottom row */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <Badge className={`text-xs border-0 ${colors.badge}`}>
          {fn.app_count} app{fn.app_count !== 1 ? "s" : ""}
        </Badge>
        {locked ? (
          <span className="text-xs text-blue-600 font-medium">Upgrade</span>
        ) : (
          <button onClick={onCardClick} className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-0.5 transition-colors">
            All <ChevronRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [functions, setFunctions] = useState([]);
  const [subcatsByFn, setSubcatsByFn] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [favourites, setFavourites] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(false);
  const [userCredits, setUserCredits] = useState(user?.credits ?? 0);

  useEffect(() => { setUserCredits(user?.credits ?? 0); }, [user]);

  const handleCreditsUpdated = (newBalance) => {
    setUserCredits(newBalance);
    // Refresh auth context user credits
    if (login && user) login({ ...user, credits: newBalance }, null);
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/functions`, { withCredentials: true }),
      axios.get(`${API}/subcategories`),
    ]).then(([fnsRes, subcatRes]) => {
      setFunctions(fnsRes.data);
      // Group subcategories by function_id
      const grouped = {};
      for (const s of subcatRes.data) {
        if (!grouped[s.function_id]) grouped[s.function_id] = [];
        grouped[s.function_id].push(s);
      }
      setSubcatsByFn(grouped);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      setLoadingFavs(true);
      axios.get(`${API}/favourites`, { withCredentials: true })
        .then(res => setFavourites(res.data))
        .catch(() => {})
        .finally(() => setLoadingFavs(false));
    }
  }, [user]);

  const handleRemoveFav = (appId) => setFavourites(prev => prev.filter(f => f.app_id !== appId));

  const filtered = functions.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleFnNav = (fn) => {
    if (!fn.has_access && fn.function_id !== "general") navigate("/pricing");
    else navigate(`/function/${fn.function_id}`);
  };

  const handleSubcatNav = (fn, subcat) => {
    if (!fn.has_access && fn.function_id !== "general") { navigate("/pricing"); return; }
    navigate(`/function/${fn.function_id}?subcategory=${subcat.subcategory_id}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-slate-500 mt-1">Browse AI applications by business function or AI tool</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-lg">
              <Coins size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-slate-900" data-testid="dashboard-credits">{userCredits} credits</span>
            </div>
            {user?.subscription_plan !== "full" && (
              <Button size="sm" onClick={() => navigate("/pricing")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upgrade-plan-btn">
                <Zap size={14} className="mr-1.5" /> Upgrade
              </Button>
            )}
          </div>
        </div>

        {/* Plan Banner */}
        {user?.subscription_plan === "free" && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Lock size={14} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">You're on the Free Plan</p>
                <p className="text-xs text-slate-500">Upgrade to access premium applications and unlock all functions</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate("/pricing")} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0">View Plans</Button>
          </div>
        )}

        {/* Browse by AI Tool */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-slate-900">Browse by AI Tool</h2>
              <p className="text-xs text-slate-500 mt-0.5">Find applications optimised for your preferred AI tool</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/apps")} className="text-blue-600 hover:text-blue-700 text-xs" data-testid="browse-all-tools-btn">
              Browse All <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {AI_TOOLS.map(tool => (
              <button
                key={tool.name}
                onClick={() => navigate(`/apps?tool=${encodeURIComponent(tool.name)}`)}
                className={`flex flex-col items-start p-3 rounded-xl border transition-all hover-lift ${tool.color}`}
                data-testid={`tool-browse-${tool.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`w-2 h-2 rounded-full mb-2 ${tool.dot}`} />
                <span className="font-semibold text-sm leading-tight">{tool.name}</span>
                <span className="text-xs opacity-70 mt-0.5 leading-tight">{tool.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* My Favourites */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="my-favourites-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Heart size={15} className="text-red-500 fill-red-500" /> My Favourites
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Apps you've saved for quick access</p>
            </div>
            {favourites.length > 0 && (
              <span className="text-xs text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100">{favourites.length} saved</span>
            )}
          </div>
          {loadingFavs ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1,2,3].map(i => <div key={i} className="border border-slate-100 rounded-xl p-4 bg-slate-50"><Skeleton className="h-3 w-16 mb-2" /><Skeleton className="h-4 w-full mb-1" /><Skeleton className="h-4 w-3/4 mb-3" /><Skeleton className="h-5 w-12" /></div>)}
            </div>
          ) : favourites.length === 0 ? (
            <div className="text-center py-7 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
              <Heart size={22} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm font-medium text-slate-500">No saved apps yet</p>
              <p className="text-xs mt-1 text-slate-400">Click the heart icon on any app to save it here</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {favourites.map(app => <FavouriteCard key={app.app_id} app={app} user={user} navigate={navigate} onRemove={handleRemoveFav} />)}
            </div>
          )}
        </div>

        {/* Browse by Function */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-slate-900">Browse by Business Function</h2>
              <p className="text-xs text-slate-500 mt-1">Click a subcategory to go directly to its applications, or click a function to browse all subcategories</p>
            </div>
          </div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search business functions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white"
              data-testid="function-search-input"
            />
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border">
                  <Skeleton className="w-10 h-10 rounded-lg mb-3" />
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-3 w-full mb-1" />
                  <Skeleton className="h-3 w-3/4 mb-4" />
                  <div className="flex gap-1.5 flex-wrap mb-3">
                    {[1,2,3,4].map(j => <Skeleton key={j} className="h-6 w-20 rounded-full" />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="functions-grid">
              {filtered.map(fn => (
                <FunctionCard
                  key={fn.function_id}
                  fn={fn}
                  subcats={subcatsByFn[fn.function_id] || []}
                  onCardClick={() => handleFnNav(fn)}
                  onSubcatClick={(s) => handleSubcatNav(fn, s)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-1 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Contribute an Application</h3>
              <p className="text-sm text-slate-500">Share your expertise with the community</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/contribute")} data-testid="dashboard-contribute-btn">Contribute</Button>
          </div>
        </div>

        {/* Gift Code + Referral */}
        <div className="grid sm:grid-cols-2 gap-4">
          <GiftCodeSection API={API} user={user} onCreditsUpdated={handleCreditsUpdated} />
          <ReferralSection API={API} user={user} />
        </div>

      </div>
    </div>
  );
};

export default Dashboard;

